import { useContext, useState } from "react";
import styled from "styled-components";
import { AuthUserContext } from "../../context/AuthUserContext";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  width: 100vw;
  min-height: calc(100vh - var(--navbar-height));
  position: fixed;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: calc(var(--zindex-fixed) + 2);
  @media (min-width: 768px) {
    min-height: 100vh;
    z-index: 99999;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  flex-direction: column;
  width: 100%;
  max-height: 90vh;
  background-color: var(--bg-100);
  position: relative;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding: var(--spacing-xxl) 0 var(--spacing-xl) 0;
  @media (min-width: 768px) {
    width: 520px;
    max-height: 80vh;
  }
`;

const FormContainer = styled.div`
  width: 84%;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xl);
`;

const Title = styled.h2`
  margin: 0 0 var(--spacing-xs) 0;
  font-size: var(--font-size-small);
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-100);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--text-200);
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddressItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-md) 0;
  border-bottom: 1px solid var(--bg-300);
  cursor: pointer;

  &:first-of-type {
    padding-top: 0;
  }
  &:last-of-type {
    border-bottom: none;
  }
`;

const RadioOuter = styled.span`
  flex-shrink: 0;
  margin-top: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid
    ${({ $selected }) => ($selected ? "var(--text-100)" : "var(--bg-300)")};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background-color: var(--text-100);
    transform: scale(${({ $selected }) => ($selected ? 1 : 0)});
    transition: transform var(--transition-fast);
  }
`;

const AddressTag = styled.p`
  margin: 0 0 4px 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-200);
`;

const AddressText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.4;
  color: var(--text-100);
`;

const Button = styled.button`
  margin-top: var(--spacing-lg);
  width: 100%;
  padding: 15px 20px;
  background-color: var(--text-100);
  color: var(--bg-100);
  border: 1px solid var(--text-100);
  border-radius: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  &:hover {
    background-color: transparent;
    color: var(--text-100);
  }
`;

const CancelButton = styled.div`
  margin-top: var(--spacing-md);
  text-decoration: underline;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  cursor: pointer;
  color: var(--text-200);

  &:hover {
    color: var(--text-100);
  }
`;

const ExitContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  width: 32px;
  height: 32px;
  cursor: pointer;
`;

const CheckoutAddresses = ({ chosenAddress, setShowConfirm, onNext }) => {
  const { t } = useTranslation();
  const { userProfile } = useContext(AuthUserContext);
  const [selectedAddress, setSelectedAddress] = useState(chosenAddress);

  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
  };

  return (
    <Container>
      <Wrapper>
        <FormContainer>
          <ExitContainer
            onClick={() => {
              setShowConfirm(null);
            }}
            role="button"
            tabIndex={0}
            aria-label={t("ARIA.CLOSE")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowConfirm(null);
              }
            }}
          >
            <svg width="18px" height="18px" viewBox="0 0 1024 1024">
              <path
                fill="var(--text-200)"
                d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"
              />
            </svg>
          </ExitContainer>

          <Header>
            <Title>{t("CHECKOUT_ADDRESSES.TITLE")}</Title>
            <Subtitle>{t("CHECKOUT_ADDRESSES.SUBTITLE")}</Subtitle>
          </Header>

          <AddressList>
            {userProfile?.addresses?.map((address) => {
              const isSelected = selectedAddress === address;
              return (
                <AddressItem
                  key={address.id}
                  onClick={() => handleSelectAddress(address)}
                >
                  <RadioOuter $selected={isSelected} />
                  <div>
                    <AddressTag>
                      {address.is_primary
                        ? t("CHECKOUT_ADDRESSES.ORIGINAL_ADDRESS")
                        : t("CHECKOUT_ADDRESSES.SUGGESTED_ADDRESS")}
                    </AddressTag>
                    <AddressText>
                      {`${address.street} ${address.street_number}`}
                    </AddressText>
                    <AddressText>
                      {`${address.city}, ${address.postal_code}`}
                    </AddressText>
                    <AddressText>{address.country}</AddressText>
                  </div>
                </AddressItem>
              );
            })}
          </AddressList>

          <Button
            onClick={() => {
              onNext(selectedAddress);
            }}
          >
            {t("BUTTONS.CONTINUE")}
          </Button>
          <CancelButton
            onClick={() => {
              setShowConfirm(null);
            }}
          >
            {t("BUTTONS.CANCEL")}
          </CancelButton>
        </FormContainer>
      </Wrapper>
    </Container>
  );
};

export default CheckoutAddresses;
